import {
  Flex,
  formatNumber,
  CurrencyValue,
  LineChart,
  formatPercentage,
  PercentageValue,
  DisplaySwitch,
  ChartSkeleton
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { useApi } from '@/api';
import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ChartTimeFilter } from '@/components';
import { useChartPrefillDatasetsDates, useChartPeriod } from '@/hooks';

export default function HistoricGraphs({
  marketAddress,
  collateralAddress,
  underlyingSymbol,
  network
}: {
  marketAddress: string;
  collateralAddress: string;
  underlyingSymbol: string;
  network: string;
}) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [activeDisplayOption, setActiveDisplayOption] =
    useState('total_supply');

  const [data, error] = useApi<SimpleChartData[]>({
    url: `/markets/${marketAddress}/collaterals/${collateralAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const displayOptions = useMemo(
    () => [
      { key: 'total_supply', value: 'Collateral Supply' },
      { key: 'utilization', value: 'Utilization' },
      { key: 'supply_cap', value: 'Supply cap' }
    ],
    []
  );

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const yScaleValueFormatter = useCallback(
    (value: string | number) => {
      switch (activeDisplayOption) {
        case 'utilization':
          return `${formatPercentage(Number(value))}`;
        default:
          return `${formatNumber(Number(value))}`;
      }
    },
    [activeDisplayOption]
  );

  const valueFormatter = useCallback(
    (value: string | number) => {
      switch (activeDisplayOption) {
        case 'utilization':
          return <PercentageValue value={value} variant="dynamic" />;
        default:
          return (
            <CurrencyValue
              value={value}
              variant="dynamic"
              options={{
                currencySize: '3',
                currency: {
                  name: underlyingSymbol,
                  address: collateralAddress,
                  network: network
                }
              }}
            />
          );
      }
    },
    [activeDisplayOption, underlyingSymbol, collateralAddress, network]
  );

  const datasets: SimpleChartDataset[] = [
    {
      xField: 'date',
      yField: activeDisplayOption
    }
  ];
  const chartData = useChartPrefillDatasetsDates(data, datasets);

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }
  return (
    <Flex flexDirection="column" gap="xs">
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      <LineChart
        key={activeDisplayOption}
        data={chartData}
        datasets={datasets}
        config={config}
        valueFormatter={valueFormatter}
        yScaleValueFormatter={yScaleValueFormatter}
        timeFilter={timeFilter}
      />
    </Flex>
  );
}
